/**
 * Mobile PDP buy box layout:
 * - Move discovery links and trust panel below the title.
 * - Desktop restores default in-form placement.
 */
const MOBILE_MQ = window.matchMedia('(max-width: 749px)');

/** @type {{ parent: Element; nextSibling: ChildNode | null } | null} */
let trustHome = null;

/** @type {{ parent: Element; nextSibling: ChildNode | null } | null} */
let discoveryHome = null;

/**
 * @returns {HTMLElement | null}
 */
function getTitleBlock() {
  return document.querySelector(
    '.template-product .product-details > .group-block > .group-block-content > .text-block:has(h1)'
  );
}

/**
 * @returns {HTMLElement | null}
 */
function getDiscoveryLinks() {
  return document.querySelector('.template-product .pdp-discovery-links');
}

/**
 * @returns {HTMLElement | null}
 */
function getTrustPanel() {
  return document.querySelector('.template-product .pdp-buybox-trust-panel');
}

/**
 * @param {HTMLElement} node
 * @param {{ parent: Element; nextSibling: ChildNode | null } | null} home
 * @returns {{ parent: Element; nextSibling: ChildNode | null }}
 */
function rememberNodeHome(node, home) {
  if (home || !node.closest('.buy-buttons-block')) return home;

  return {
    parent: node.parentElement,
    nextSibling: node.nextElementSibling,
  };
}

/**
 * @param {HTMLElement} node
 * @param {{ parent: Element; nextSibling: ChildNode | null } | null} home
 */
function restoreNode(node, home) {
  if (!home) return;

  const { parent, nextSibling } = home;
  if (!(parent instanceof HTMLElement)) return;

  if (nextSibling && nextSibling.parentElement === parent) {
    parent.insertBefore(node, nextSibling);
  } else {
    parent.appendChild(node);
  }
}

/**
 * @param {HTMLElement} node
 * @param {HTMLElement} anchor
 */
function placeAfterAnchor(node, anchor) {
  if (node.parentElement !== anchor.parentElement || node.previousElementSibling !== anchor) {
    anchor.after(node);
  }
}

/**
 * @param {HTMLElement} title
 * @param {HTMLElement | null} discovery
 * @param {HTMLElement | null} trust
 */
function relocateMobileBuyboxExtras(title, discovery, trust) {
  const detailsContent = title.parentElement;
  if (!(detailsContent instanceof HTMLElement)) return;

  let anchor = title;

  if (discovery) {
    placeAfterAnchor(discovery, anchor);
    discovery.setAttribute('data-pdp-mobile-discovery-relocated', 'true');
    anchor = discovery;
  }

  if (trust) {
    placeAfterAnchor(trust, anchor);
    trust.setAttribute('data-pdp-mobile-trust-relocated', 'true');
  }
}

/**
 * @param {HTMLElement | null} discovery
 * @param {HTMLElement | null} trust
 */
function restoreDesktopBuyboxExtras(discovery, trust) {
  if (discovery) {
    restoreNode(discovery, discoveryHome);
    discovery.removeAttribute('data-pdp-mobile-discovery-relocated');
  }

  if (trust) {
    restoreNode(trust, trustHome);
    trust.removeAttribute('data-pdp-mobile-trust-relocated');
  }
}

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function getSwatchChoiceLabel(input) {
  const aria = input.getAttribute('aria-label') || '';
  const soldOutSuffix = ' - ';
  const soldOutIndex = aria.indexOf(soldOutSuffix);
  if (soldOutIndex !== -1) {
    return aria.slice(0, soldOutIndex).trim();
  }
  return aria.trim() || input.value;
}

/**
 * @param {HTMLFieldSetElement} fieldset
 */
function syncFieldsetSwatchValueLabel(fieldset) {
  const checked = fieldset.querySelector('input[type="radio"]:checked');
  const below = fieldset.querySelector('.variant-option__swatch-value--below');
  const legendValue = fieldset.querySelector('.variant-option__heading .variant-option__swatch-value');

  if (!(checked instanceof HTMLInputElement) || !below) return;

  const label = getSwatchChoiceLabel(checked);
  below.textContent = label;
  if (legendValue) legendValue.textContent = label;
}

export function syncMobileSwatchValueLabels() {
  if (!MOBILE_MQ.matches) return;

  document
    .querySelectorAll('.template-product fieldset.variant-option--swatches')
    .forEach((fieldset) => {
      if (fieldset instanceof HTMLFieldSetElement) {
        syncFieldsetSwatchValueLabel(fieldset);
      }
    });
}

export function syncMobileBuyboxLayout() {
  const title = getTitleBlock();
  const discovery = getDiscoveryLinks();
  const trust = getTrustPanel();

  if (!title && !discovery && !trust) return false;

  if (discovery) {
    discoveryHome = rememberNodeHome(discovery, discoveryHome);
  }

  if (trust) {
    trustHome = rememberNodeHome(trust, trustHome);
  }

  if (MOBILE_MQ.matches && title) {
    relocateMobileBuyboxExtras(title, discovery, trust);
    syncMobileSwatchValueLabels();
  } else {
    restoreDesktopBuyboxExtras(discovery, trust);
  }

  document.dispatchEvent(new CustomEvent('pdp-mobile-buybox:synced'));
  return true;
}

document.addEventListener('DOMContentLoaded', syncMobileBuyboxLayout);
MOBILE_MQ.addEventListener('change', syncMobileBuyboxLayout);
document.addEventListener('shopify:section:load', syncMobileBuyboxLayout);
document.addEventListener('pdp-post-atc:synced', syncMobileBuyboxLayout);
document.addEventListener('variant:update', () => {
  syncMobileSwatchValueLabels();
});

document.addEventListener(
  'change',
  (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
    const fieldset = event.target.closest('fieldset.variant-option--swatches');
    if (!(fieldset instanceof HTMLFieldSetElement) || !MOBILE_MQ.matches) return;
    syncFieldsetSwatchValueLabel(fieldset);
  },
  true
);
window.addEventListener('pagereveal', syncMobileBuyboxLayout);
window.addEventListener('pageshow', (event) => {
  if (event.persisted) syncMobileBuyboxLayout();
});

if (document.readyState !== 'loading') {
  syncMobileBuyboxLayout();
}
